import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { DeleteAttachmentCommand } from './delete-attachment.command';
import { DeleteAttachmentResult } from './delete-attachment.result';

/**
 * Delete attachment use case.
 * Pure business logic - deletes attachment from backend and S3.
 *
 * No state management or error mapping - component handles all presentation concerns.
 * Follows Clean Architecture: use case orchestrates business logic only.
 */
@Injectable({ providedIn: 'root' })
export class DeleteAttachmentUseCase {
  private readonly _repository = inject(CasesRepository);

  /**
   * Executes attachment deletion from backend and S3 storage.
   *
   * Pure use case - no state management or side effects. Calling component
   * is responsible for managing loading states, updating data lists, and
   * presenting results/errors to the user.
   *
   * @param command - Delete command containing caseId and attachmentId
   * @returns Observable that emits success result on completion
   *
   * @throws AttachmentError with appropriate type:
   *   - CASE_CLOSED: case is closed or resolved
   *   - PERMISSION_DENIED: user lacks delete permissions
   *   - NOT_FOUND: attachment doesn't exist
   *   - NETWORK_ERROR: network connectivity issues
   *   - UNKNOWN: unexpected errors
   *
   * Observable behavior:
   *   - Emits once: { success: true } when deletion completes
   *   - Completes: after successful deletion
   *   - Errors: if backend/S3 deletion fails (throws AttachmentError)
   *
   * Component responsibilities:
   *   - Set loading state before calling execute()
   *   - Remove attachment from state on success
   *   - Clear loading state in finalize()
   *   - Map errors to user messages via AttachmentErrorMapperService
   *   - Display toast notifications
   */
  public execute(command: DeleteAttachmentCommand): Observable<DeleteAttachmentResult> {
    return this._repository.deleteAttachment(command.caseId, command.attachmentId).pipe(
      map(() => ({ success: true }))
    );
  }
}
